import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, Copy } from "lucide-react";
import { Brand, LanguageToggle } from "../components/Brand";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [demoLink, setDemoLink] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
      if (data.demo_reset_link) {
        // Rewrite origin to current window origin (backend may have captured the K8s cluster URL)
        try {
          const u = new URL(data.demo_reset_link);
          setDemoLink(`${window.location.origin}${u.pathname}${u.search}`);
        } catch {
          setDemoLink(data.demo_reset_link);
        }
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(demoLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center justify-between">
          <Brand />
          <LanguageToggle />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">{t("reset_password")}</h1>
        {!done ? (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("email")}
                data-testid="forgot-email"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
            {error && <div className="text-sm text-rose-600" data-testid="forgot-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("send_reset_link")}
            </button>
          </form>
        ) : (
          <div className="mt-6 space-y-4" data-testid="forgot-success">
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
              {t("reset_sent")}
            </div>
            {demoLink && (
              <div className="text-xs text-slate-700 bg-amber-50 border border-amber-100 rounded-xl p-3">
                <div className="font-medium text-amber-900 mb-1">{t("reset_link_demo")}</div>
                <div className="flex items-center gap-2">
                  <a href={demoLink} className="text-blue-700 underline break-all flex-1" data-testid="forgot-demo-link">
                    {demoLink}
                  </a>
                  <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50" data-testid="forgot-copy-link">
                    <Copy className="w-3 h-3" /> {copied ? t("copied") : t("copy")}
                  </button>
                </div>
              </div>
            )}
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900" data-testid="forgot-back-login">
              <ArrowLeft className="w-4 h-4" /> {t("go_to_login")}
            </Link>
          </div>
        )}
        <div className="mt-8 text-sm text-slate-500">
          <Link to="/login" className="inline-flex items-center gap-2 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> {t("login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
